import {
  CommunicationChannelType,
  ConsentData,
  ConsentStateType,
  ConsentType,
} from '@axo/shared/data-access/types/consent';
import { MarketCountryCode } from '@axo/shared/types';
import { hasRealAiPhoneMarketing } from './consentHandlers';

/** One partner's consent items, grouped by channel. */
export type ConsentByChannel = Record<string, ConsentData[]>;

const isGiven = (byChannel: ConsentByChannel | undefined, channel: string) =>
  byChannel?.[channel]?.find((item) => !item.Transactional)?.State ===
  ConsentStateType.Given;

/**
 * The "Marketing consent (EMAIL or SMS[ or Call])" YES/NO row: given when the
 * partner's Email or SMS marketing consent is given, or Call in Finland.
 * Needs both an Email and an SMS item; without them it reads NO.
 */
export const isMarketingConsentGiven = (
  byChannel: ConsentByChannel | undefined,
  market: string | undefined
): boolean =>
  !!byChannel?.[CommunicationChannelType.Email] &&
  !!byChannel?.[CommunicationChannelType.Sms] &&
  (isGiven(byChannel, CommunicationChannelType.Email) ||
    isGiven(byChannel, CommunicationChannelType.Sms) ||
    (market === MarketCountryCode.Finland &&
      isGiven(byChannel, CommunicationChannelType.Phone)));

/**
 * True when the partner's marketing consent is already revoked, so "Revoke
 * for <partner>" has nothing to revoke: Email and SMS not given, and Call not
 * given in Finland. Outside Finland the Call term used to be `false`, which
 * made this always false there (MV-3323).
 */
export const isMarketingConsentRevoked = (
  byChannel: ConsentByChannel | undefined,
  market: string | undefined
): boolean =>
  !isGiven(byChannel, CommunicationChannelType.Email) &&
  !isGiven(byChannel, CommunicationChannelType.Sms) &&
  (market === MarketCountryCode.Finland
    ? !isGiven(byChannel, CommunicationChannelType.Phone)
    : true);

type MarketingChanges = Partial<
  Record<
    CommunicationChannelType,
    { [ConsentType.Marketing]: ConsentStateType }
  >
>;

export interface ConsentPatch {
  PartnerChanges: Record<string, MarketingChanges>;
  OriginAction: 'ACE';
  OriginBrandID: string | undefined;
  CustomerID: string | undefined;
  Country: string | undefined;
}

interface RevokeContext {
  market: string | undefined;
  brand: string | undefined;
  customerID: string;
  /** The raw consent items, never the normalized ones (see hasRealAiPhoneMarketing). */
  rawConsentItems: ConsentData[] | undefined;
}

const revoked = { [ConsentType.Marketing]: ConsentStateType.Revoked };

/** Every marketing channel "Revoke" turns off for one partner. */
const marketingRevokeChanges = (
  partnerId: number | undefined,
  { market, rawConsentItems }: RevokeContext
): MarketingChanges => ({
  [CommunicationChannelType.Email]: revoked,
  [CommunicationChannelType.Sms]: revoked,
  ...(market === MarketCountryCode.Finland
    ? { [CommunicationChannelType.Phone]: revoked }
    : {}),
  // AI phone is marketing-only. Only revoke it when a real item exists for
  // this partner — the change endpoint 404s on a missing item and fails the
  // whole bulk revoke, and legacy customers have no ai_phone item.
  ...(hasRealAiPhoneMarketing(rawConsentItems, partnerId)
    ? { [CommunicationChannelType.AiPhone]: revoked }
    : {}),
});

/** One channel switch: sets that channel's marketing consent, nothing else. */
export const buildChannelPatch = (
  consentData: ConsentData,
  given: boolean
): ConsentPatch => ({
  PartnerChanges: {
    [consentData.PartnerID]: {
      [consentData.Channel]: {
        [ConsentType.Marketing]: given
          ? ConsentStateType.Given
          : ConsentStateType.Revoked,
      },
    },
  },
  OriginAction: 'ACE',
  OriginBrandID: consentData.OriginBrandID,
  CustomerID: consentData.CustomerID,
  Country: consentData.Country,
});

/** "Revoke for <partner>" and the master "Marketing consent" toggle. */
export const buildRevokeForPartnerPatch = (
  partnerId: number | undefined,
  context: RevokeContext
): ConsentPatch => ({
  PartnerChanges: {
    [`${partnerId}`]: marketingRevokeChanges(partnerId, context),
  },
  OriginAction: 'ACE',
  OriginBrandID: context.brand,
  CustomerID: context.customerID,
  Country: context.market,
});

/** "Revoke for all": the same revoke for every listed partner. */
export const buildRevokeForAllPatch = (
  partnerIds: number[],
  context: RevokeContext
): ConsentPatch => ({
  PartnerChanges: Object.fromEntries(
    partnerIds.map((id) => [id, marketingRevokeChanges(id, context)])
  ),
  OriginAction: 'ACE',
  OriginBrandID: context.brand,
  CustomerID: context.customerID,
  Country: context.market,
});

/**
 * True when any listed partner has a given marketing item. Reads the raw
 * items: the normalized ones include synthesized rows.
 */
export const hasAnyGivenMarketing = (
  rawConsentItems: ConsentData[] | undefined,
  partnerIds: number[]
): boolean =>
  !!rawConsentItems?.some(
    (item) =>
      partnerIds.includes(item.PartnerID) &&
      !item.Transactional &&
      item.State === ConsentStateType.Given
  );
